import * as React from "react"

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { cva } from "class-variance-authority"
import { X } from "lucide-react"

import { cn } from "../../cn"
import { Button } from "../Button/Button"
import {
  DrawerResizeHandle,
  useDockWidthReservation,
  useInlineDockResize,
  type DockSize,
} from "./DrawerInlineDock"

/* ── Variants ─────────────────────────────────────────────── */

// One presentation: the drawer docks beside the page content rather than
// floating a sheet over it. It anchors its right edge at the Sofia dock width
// (0px when Sofia is closed via the :root default) so the two panels sit side
// by side, and it wears the app shell's chrome surface — the same background as
// the sidebar and the Sofia dock — to read as part of the layout.
//
// No width class: the dock owns a resizable width and applies it as an inline
// style, so a `w-*` here would either lose to it or fight it. The bounds are
// only the outer envelope — a 24rem reserve for the page content the drawer
// squeezes, since that content stays visible and interactive instead of being
// covered, and a 20rem floor that keeps the drawer usable on narrow viewports,
// where the content scrolls instead. Both mirror the JS clamp in
// DrawerInlineDock, and hold even when the Sofia dock is dragged wider
// underneath a stored width.
const drawerContentVariants = cva([
  "fixed inset-y-0 z-50 flex h-full flex-col",
  "right-[var(--covi-dock-width)] border-l border-coval-border",
  "min-w-[20rem] max-w-[calc(100vw-var(--covi-dock-width)-24rem)]",
  "bg-surface-contrast text-coval-text shadow-none",
  "transition-transform duration-300 ease-in-out",
  "data-[state=open]:animate-in data-[state=open]:slide-in-from-right",
  "data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right",
])

/* ── Inline docking ───────────────────────────────────────── */

// The content reserves its width for as long as it is open. Radix keeps the
// node mounted through the exit animation, so the drawer's own open state —
// not its mount — is what says whether the reservation still stands.
const DrawerOpenContext = React.createContext(false)

/* ── Root ──────────────────────────────────────────────────── */

export type DrawerProps = Omit<
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
  "onOpenChange" | "modal"
> & {
  onOpenChange?: (open: boolean) => void
}

/**
 * A panel docked beside the page content. The drawer drops the scrim, keeps the
 * rest of the page interactive (no focus trap, no scroll lock, no
 * dismiss-on-outside-click), and reserves its width from the app shell's
 * content region. Close it with the close button or Escape.
 *
 * For flows that must block the page until they are answered, reach for Dialog
 * or AlertDialog instead — a drawer never covers the work behind it.
 */
const Drawer: React.FC<DrawerProps> = ({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(
    defaultOpen ?? false,
  )
  const isOpen = open ?? uncontrolledOpen

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [onOpenChange],
  )

  return (
    <DrawerOpenContext.Provider value={isOpen}>
      <DialogPrimitive.Root
        modal={false}
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DrawerOpenContext.Provider>
  )
}
Drawer.displayName = "Drawer"

const DrawerTrigger = DialogPrimitive.Trigger
const DrawerClose = DialogPrimitive.Close

/* ── Content ──────────────────────────────────────────────── */

export interface DrawerContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> {
  /**
   * How much room the drawer's content asks for when it opens: `sm` for a
   * single column of detail, `lg` for forms, tables, or code that need the
   * width to stay readable. Only the opening width — the drag handle wins from
   * there, and each size remembers what it was last dragged to.
   */
  size?: DockSize
}

const DrawerContent = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ className, size = "md", style, children, ...props }, ref) => {
  const open = React.useContext(DrawerOpenContext)
  const measureRef = useDockWidthReservation(open)
  const resize = useInlineDockResize(open, size)
  const contentRef = React.useRef<HTMLDivElement | null>(null)

  const setRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node
      measureRef(node)
      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ref.current = node
      }
    },
    [measureRef, ref],
  )

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Content
        ref={setRef}
        className={cn(drawerContentVariants(), className)}
        style={{ width: resize.width, ...style }}
        // The drawer sits beside the content rather than over it, so
        // interacting with the page it leaves visible must not dismiss it.
        onInteractOutside={(event) => event.preventDefault()}
        // Focus lands on the panel itself, so a screen reader announces the
        // drawer by its title and the next Tab reaches its first control,
        // without jumping into a form field the user has not chosen yet.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          contentRef.current?.focus({ preventScroll: true })
        }}
        {...props}
      >
        <DrawerResizeHandle
          width={resize.width}
          setWidth={resize.setWidth}
          isResizing={resize.isResizing}
          setResizing={resize.setResizing}
        />
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  )
})
DrawerContent.displayName = "DrawerContent"

/* ── Header ───────────────────────────────────────────────── */

export interface DrawerHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Controls that sit before the close button, e.g. "open in new tab". */
  actions?: React.ReactNode
  /**
   * Drop the close button. For a drawer whose body already ends in an explicit
   * Cancel — a form the user commits or abandons — a second dismissal in the
   * corner is one exit too many.
   */
  hideClose?: boolean
}

const DrawerHeader = React.forwardRef<HTMLDivElement, DrawerHeaderProps>(
  ({ className, actions, hideClose = false, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // The drawer's header is a top bar like Sofia's: same `min-h-17` /
        // `py-2` box and the same divider, so the two panels and the app-shell
        // top bar they sit beside all share one baseline and one rule line.
        // The controls ride inside that row rather than floating over it, so
        // they stay centred on the bar and never cross the divider, however
        // tall the title and description make it.
        "flex min-h-17 shrink-0 items-center gap-2 border-b border-coval-border px-4 py-2",
        className,
      )}
      {...props}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">{children}</div>
      <div className="flex shrink-0 items-center gap-2">
        {actions}
        {!hideClose && (
          <DrawerClose asChild>
            <Button variant="secondary" icon tooltip="Close">
              <X className="h-4 w-4" />
            </Button>
          </DrawerClose>
        )}
      </div>
    </div>
  ),
)
DrawerHeader.displayName = "DrawerHeader"

/* ── Body ─────────────────────────────────────────────────── */

const DrawerBody = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    // A container, so content inside can lay itself out against the dock's
    // width rather than the viewport's — the drawer is dragged to any width
    // between its floor and cap, and viewport breakpoints say nothing about
    // how much room the content actually got.
    className={cn("@container flex-1 overflow-y-auto px-4 py-3", className)}
    {...props}
  />
))
DrawerBody.displayName = "DrawerBody"

/* ── Footer ───────────────────────────────────────────────── */

const DrawerFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center justify-end gap-2 px-4 pt-0 pb-4",
      className,
    )}
    {...props}
  />
))
DrawerFooter.displayName = "DrawerFooter"

/* ── Title ────────────────────────────────────────────────── */

const DrawerTitle = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-sm font-semibold text-coval-text", className)}
    {...props}
  />
))
DrawerTitle.displayName = "DrawerTitle"

/* ── Description ──────────────────────────────────────────── */

const DrawerDescription = React.forwardRef<
  React.ComponentRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-coval-text-muted", className)}
    {...props}
  />
))
DrawerDescription.displayName = "DrawerDescription"

/* ── Exports ──────────────────────────────────────────────── */

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  drawerContentVariants,
}
